import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import axios from 'axios';

// Types
interface Admin {
  id: number;
  name: string;
  email: string;
  role: 'super_admin' | 'admin' | 'sub_admin';
  profile_picture?: string;
  status: 'active' | 'inactive';
  last_login?: string;
  created_at: string;
}

interface AuthState {
  admin: Admin | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  forgotPassword: (email: string) => Promise<{ success: boolean; message: string }>;
  resetPassword: (token: string, password: string) => Promise<{ success: boolean; message: string }>;
  updateProfile: (data: { name: string; email: string }) => Promise<{ success: boolean; message: string }>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; message: string }>;
  refreshAuth: () => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Auth Provider Component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    admin: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Setup axios interceptors
  useEffect(() => {
    // Request interceptor
    const requestInterceptor = api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('skillseek_admin_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If error is 401 and we haven't tried to refresh token yet
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          // Only try to refresh if we have a token
          const token = localStorage.getItem('skillseek_admin_token');
          if (token) {
            try {
              // Try to verify token again
              await verifyToken();
              
              // Retry the original request
              const newToken = localStorage.getItem('skillseek_admin_token');
              if (newToken) {
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return api(originalRequest);
              }
            } catch (refreshError) {
              // If refresh fails, logout
              console.log('Token refresh failed, logging out');
              logout();
              return Promise.reject(refreshError);
            }
          } else {
            // No token, logout
            logout();
          }
        }

        return Promise.reject(error);
      }
    );

    // Cleanup interceptors on unmount
    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const hasVerifiedRef = useRef(false);

  // Verify token on app load
  const verifyToken = useCallback(async () => {
    // Prevent multiple simultaneous verifications
    if (hasVerifiedRef.current) return;
    hasVerifiedRef.current = true;

    try {
      const token = localStorage.getItem('skillseek_admin_token');
      if (!token) {
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      const response = await api.get('/auth/verify-token');
      if (response.data.success) {
        setAuthState(prev => ({
          ...prev,
          admin: response.data.admin,
          token,
          isAuthenticated: true,
          isLoading: false,
        }));
      } else {
        console.log('Token verification failed:', response.data.message);
        // Inline logout logic to avoid dependency issues
        localStorage.removeItem('skillseek_admin_token');
        delete api.defaults.headers.common['Authorization'];
        setAuthState({
          admin: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    } catch (error: any) {
      console.log('Token verification error:', error.response?.data?.message || error.message);
      // Only logout if it's a clear authentication error, not a network error
      if (error.response?.status === 401) {
        // Inline logout logic to avoid dependency issues
        localStorage.removeItem('skillseek_admin_token');
        delete api.defaults.headers.common['Authorization'];
        setAuthState({
          admin: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
      } else {
        // For network errors, keep the current state but stop loading
        setAuthState(prev => ({ ...prev, isLoading: false }));
      }
    }
  }, []);

  // Load token on mount
  useEffect(() => {
    const token = localStorage.getItem('skillseek_admin_token');
    if (token) {
      setAuthState(prev => ({ ...prev, token }));
      verifyToken();
    } else {
      setAuthState(prev => ({ ...prev, isLoading: false }));
    }
  }, [verifyToken]);

  // Login function
  const login = async (email: string, password: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      console.log('Attempting login with email:', email);
      const response = await api.post('/auth/login', { email, password });
      console.log('Login response:', response.data);
      
      if (response.data.success) {
        const { token, admin } = response.data;
        
        // Store token
        localStorage.setItem('skillseek_admin_token', token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        setAuthState({
          admin,
          token,
          isAuthenticated: true,
          isLoading: false,
        });
        
        return { success: true, message: response.data.message };
      } else {
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return { success: false, message: response.data.message };
      }
    } catch (error: any) {
      console.error('Login error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers
      });
      
      setAuthState(prev => ({ ...prev, isLoading: false }));
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed. Please try again.' 
      };
    }
  };

  // Logout function
  const logout = () => {
    const token = localStorage.getItem('skillseek_admin_token');
    
    localStorage.removeItem('skillseek_admin_token');
    delete api.defaults.headers.common['Authorization'];
    
    setAuthState({
      admin: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
    
    // Only call logout endpoint if we have a valid token and are authenticated
    if (token && authState.isAuthenticated) {
      api.post('/auth/logout').catch(error => {
        console.error('Logout logging failed:', error);
      });
    }
  };

  // Forgot password function
  const forgotPassword = async (email: string) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return { 
        success: response.data.success, 
        message: response.data.message 
      };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to send reset email.' 
      };
    }
  };

  // Reset password function
  const resetPassword = async (token: string, password: string) => {
    try {
      const response = await api.post('/auth/reset-password', { token, password });
      return { 
        success: response.data.success, 
        message: response.data.message 
      };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to reset password.' 
      };
    }
  };

  // Update profile function
  const updateProfile = async (data: { name: string; email: string }) => {
    try {
      const response = await api.put('/auth/profile', data);
      if (response.data.success) {
        // Update local state with new data
        setAuthState(prev => ({
          ...prev,
          admin: response.data.admin,
        }));
      }
      return { 
        success: response.data.success, 
        message: response.data.message 
      };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to update profile.' 
      };
    }
  };

  // Change password function
  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      const response = await api.put('/auth/change-password', {
        currentPassword,
        newPassword,
      });
      return { 
        success: response.data.success, 
        message: response.data.message 
      };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to change password.' 
      };
    }
  };

  // Refresh auth data
  const refreshAuth = async () => {
    try {
      const response = await api.get('/auth/profile');
      if (response.data.success) {
        setAuthState(prev => ({
          ...prev,
          admin: response.data.admin,
        }));
      }
    } catch (error) {
      console.error('Failed to refresh auth data:', error);
    }
  };

  // Context value
  const contextValue: AuthContextType = {
    ...authState,
    login,
    logout,
    forgotPassword,
    resetPassword,
    updateProfile,
    changePassword,
    refreshAuth,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Export axios instance for use in other parts of the app
export { api }; 